/* SL - Sticky add to cart (sections/sl-sticky-atc.liquid).
   Shows the bar while the page's main add-to-cart button is scrolled away above the screen (and the footer
   isn't in view). Clicking submits the main product form with its own button as submitter, so the buy box's
   add-to-cart code runs (sl-pdp-main's <sl-buy> or Refresh's <product-form>) and opens the cart drawer.
   Label, price and disabled state are mirrored from the main button whenever the buy box changes. */
(() => {
  if (customElements.get('sl-sticky-atc')) return;

  class SlStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.findForm();
      if (!this.form) return; // no buy box on the page: stay hidden
      this.mainBtn = this.form.querySelector('[type="submit"][name="add"]');
      this.root = this.form.closest('sl-buy, product-form') || this.form;
      this.buy = this.form.closest('sl-buy');
      this.priceSource = this.buy ? null : document.getElementById(`price-${this.form.id.replace(/^product-form-/, '')}`);
      this.btn = this.querySelector('[data-satc-button]');
      this.label = this.querySelector('[data-satc-label]');
      this.price = this.querySelector('[data-satc-price]');
      this.strike = this.querySelector('[data-satc-strike]');
      this.past = false;
      this.footer = false;
      this.forced = false;

      this.btn.addEventListener('click', () => this.add());

      // Position is measured on scroll (once per frame) rather than with IntersectionObserver: a fast flick or an
      // anchor jump can take the button from below the screen to above it without ever crossing it, and
      // IntersectionObserver only reports crossings.
      this.footerEl = document.querySelector('.footer, footer');
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.measure();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.queueSync = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.sync());
      };
      this.mo = new MutationObserver(this.queueSync);
      const opts = { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class'] };
      this.mo.observe(this.root, opts);
      if (this.priceSource) this.mo.observe(this.priceSource, opts);
      this.root.addEventListener('change', this.queueSync);

      // Theme editor: show the bar while its section is selected.
      this.onSelect = (e) => this.editorSelect(e, true);
      this.onDeselect = (e) => this.editorSelect(e, false);
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);

      this.removeAttribute('hidden');
      this.sync();
      this.measure();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.mo?.disconnect();
      this.root?.removeEventListener('change', this.queueSync);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
    }

    findForm() {
      return [...document.querySelectorAll('main form[action*="/cart/add"]')].find(
        (f) => !this.contains(f) && !f.closest('quick-add-modal, .quick-add-modal') && f.querySelector('[type="submit"][name="add"]')
      ) || null;
    }

    editorSelect(e, on) {
      if (e.detail?.sectionId !== this.dataset.section) return;
      this.forced = on;
      this.render();
    }

    measure() {
      this.past = this.mainBtn.getBoundingClientRect().bottom < 0;
      this.footer = !!this.footerEl && this.footerEl.getBoundingClientRect().top < window.innerHeight;
      this.render();
    }

    render() {
      const on = this.forced || (this.past && !this.footer);
      this.classList.toggle('is-visible', on);
      this.toggleAttribute('inert', !on);
    }

    sync() {
      const main = this.mainBtn;
      this.btn.disabled = main.disabled;
      const text = (main.querySelector('[data-cta-label]') || main.querySelector('span') || main).textContent.trim();
      if (text) this.label.textContent = text;
      if (this.price) this.syncPrice();
      if (this.pending && this.hasError()) {
        this.pending = false;
        main.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    syncPrice() {
      let now = '';
      let was = '';
      if (this.buy) {
        const total = this.buy.querySelector('[data-cta-price]');
        if (total) {
          now = total.textContent;
          const s = this.buy.querySelector('[data-cta-strike]');
          was = s && !s.hidden ? s.textContent : '';
        } else {
          const sub = this.form.querySelector('input[name="sl-purchase"]:checked')?.value === 'sub';
          now = this.buy.querySelector(sub ? '[data-price-sub]' : '[data-price-once]')?.textContent || '';
        }
      } else if (this.priceSource) {
        const onSale = this.priceSource.querySelector('.price--on-sale');
        now = this.priceSource.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular')?.textContent || '';
        was = onSale ? this.priceSource.querySelector('.price__sale .price-item--regular')?.textContent || '' : '';
      }
      if (now.trim()) this.price.textContent = now.trim();
      this.strike.hidden = !was.trim();
      if (was.trim()) this.strike.textContent = was.trim();
    }

    hasError() {
      const el = this.root.querySelector('[data-error], .product-form__error-message-wrapper');
      return !!el && !el.hidden && el.textContent.trim() !== '';
    }

    add() {
      if (this.btn.disabled || this.mainBtn.disabled) return;
      // If the add fails, the buy box shows the message; bring it into view then.
      this.pending = true;
      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => { this.pending = false; }, 10000);
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.mainBtn);
      else this.mainBtn.click();
    }
  }

  customElements.define('sl-sticky-atc', SlStickyAtc);
})();
